import { useCallback, useRef, type RefObject } from 'react';

/** Geometry relative to the nearest moving ancestor, avoiding doubled movement. */
interface LayoutBox { x: number; y: number; height: number }

/** Animate layout changes with transforms; text is never scaled with its surface. */
export function useLayoutMotion(root: RefObject<HTMLElement | null>) {
  const previous = useRef<Map<string, LayoutBox> | null>(null);
  const running = useRef<Animation[]>([]);

  /** Read rendered positions, including any in-progress interrupted transition. */
  const measure = useCallback(() => {
    const result = new Map<string, LayoutBox>();
    root.current?.querySelectorAll<HTMLElement>('[data-motion-key]').forEach(element => {
      const rect = element.getBoundingClientRect();
      const parent = element.parentElement?.closest<HTMLElement>('[data-motion-key]')?.getBoundingClientRect();
      result.set(element.dataset.motionKey!, { x: rect.left - (parent?.left ?? 0), y: rect.top - (parent?.top ?? 0), height: rect.height });
    });
    return result;
  }, [root]);

  const capture = useCallback(() => {
    previous.current = measure();
    running.current.forEach(animation => animation.cancel());
    running.current = [];
  }, [measure]);

  /** Commit geometry once, then animate only transform and opacity on the compositor. */
  const play = useCallback(() => {
    const before = previous.current;
    previous.current = null;
    if (!before || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const after = measure();
    const timing = { duration: 600, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' };
    root.current?.querySelectorAll<HTMLElement>('[data-motion-key]').forEach(element => {
      const key = element.dataset.motionKey!;
      const old = before.get(key);
      const current = after.get(key);
      if (!current || current.height === 0 || !element.animate) return;
      if (!old || old.height === 0) {
        running.current.push(element.animate([{ opacity: 0, transform: 'translateY(24px)' }, { opacity: 1, transform: 'translateY(0)' }], timing));
        return;
      }
      const x = old.x - current.x;
      const y = old.y - current.y;
      if (Math.abs(x) > 0.5 || Math.abs(y) > 0.5) {
        running.current.push(element.animate([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'translate(0, 0)' }], timing));
      }
      // The faint section wash resizes independently, keeping question text at full scale.
      if (element.classList.contains('question-section') && Math.abs(old.height - current.height) > 0.5) {
        running.current.push(element.animate([{ transform: `scaleY(${(old.height + 16) / (current.height + 16)})` }, { transform: 'scaleY(1)' }], { ...timing, pseudoElement: '::before' }));
      }
    });
  }, [measure, root]);

  return { capture, play };
}
